import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  RenderTask
} from "pdfjs-dist";
import Spinner from "@/components/ui/spinner";
import Text from "@/components/ui/text";
import { getDocument } from "../pdfjs";

// The colour painted at a point on the page, in PDF points; undefined off the
// page or before the page has finished drawing.
export type SampleColor = (x: number, y: number) => string | undefined;

// Draws over one page; scale converts PDF points to CSS pixels.
export type PageOverlay = (
  pageNumber: number,
  scale: number,
  sampleColor?: SampleColor
) => React.ReactNode;

interface PageProps {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  onError: () => void;
  renderOverlay?: PageOverlay;
}

// pdf.js rejects a cancelled render with this error; only other rejections
// mean the page could not be drawn.
const isCancelledRender = (error: unknown) =>
  (error as { name?: string } | null)?.name === "RenderingCancelledException";

const PdfPage: React.FC<PageProps> = ({
  pdf,
  pageNumber,
  width,
  onError,
  renderOverlay
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [scale, setScale] = useState<number>();
  const [sampleColor, setSampleColor] = useState<SampleColor>();

  useEffect(() => {
    let renderTask: RenderTask | undefined;
    let cancelled = false;
    setSampleColor(undefined);
    pdf
      .getPage(pageNumber)
      .then((page) => {
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        const pageScale = width / page.getViewport({ scale: 1 }).width;
        setScale(pageScale);
        const pixelRatio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: pageScale * pixelRatio });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${viewport.height / pixelRatio}px`;
        const context = canvas.getContext("2d");
        if (!context) return;
        renderTask = page.render({ canvasContext: context, viewport });
        const pixelsPerPoint = pageScale * pixelRatio;
        renderTask.promise.then(
          () => {
            if (cancelled) return;
            setSampleColor(() => (x: number, y: number) => {
              const pixelX = Math.floor(x * pixelsPerPoint);
              const pixelY = Math.floor(y * pixelsPerPoint);
              if (
                pixelX < 0 ||
                pixelY < 0 ||
                pixelX >= canvas.width ||
                pixelY >= canvas.height
              )
                return undefined;
              try {
                const [red, green, blue, alpha] = context.getImageData(
                  pixelX,
                  pixelY,
                  1,
                  1
                ).data;
                return alpha === 0
                  ? undefined
                  : `rgb(${red}, ${green}, ${blue})`;
              } catch {
                return undefined;
              }
            });
          },
          (error) => {
            if (!cancelled && !isCancelledRender(error)) onError();
          }
        );
      })
      // Destroying the document on unmount rejects a pending page by design.
      .catch(() => {
        if (!cancelled) onError();
      });
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, width, onError]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className="block rounded-2xl bg-white"
        data-page-number={pageNumber}
        data-test="proposal-document-page"
      />
      {scale && renderOverlay
        ? renderOverlay(pageNumber, scale, sampleColor)
        : null}
    </div>
  );
};

interface Props {
  url: string;
  width: number;
  onPageCount?: (pageCount: number) => void;
  renderOverlay?: PageOverlay;
}

// Draws the server-composed bytes as-is; nothing about the document is built
// in the browser.
export const PdfDocument: React.FC<Props> = ({
  url,
  width,
  onPageCount,
  renderOverlay
}) => {
  const { t } = useTranslation();
  const [pdf, setPdf] = useState<PDFDocumentProxy>();
  const [failed, setFailed] = useState(false);
  const onPageError = useCallback(() => setFailed(true), []);
  // The task whose document is on screen, and those it replaced.
  const shownTask = useRef<PDFDocumentLoadingTask>();
  const replacedTasks = useRef<PDFDocumentLoadingTask[]>([]);

  useEffect(() => {
    if (pdf) onPageCount?.(pdf.numPages);
  }, [pdf, onPageCount]);

  // A new url keeps the current document on screen until its own has loaded,
  // so the pages, and where the rep has scrolled to, stay put meanwhile.
  useEffect(() => {
    // No eval: a PDF's function code is interpreted, never compiled to JS.
    const loadingTask = getDocument({ url, isEvalSupported: false });
    let cancelled = false;
    loadingTask.promise
      .then((loaded) => {
        if (cancelled) return;
        if (shownTask.current) replacedTasks.current.push(shownTask.current);
        shownTask.current = loadingTask;
        setFailed(false);
        setPdf(loaded);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      // A newer url gets its own chance to load.
      setFailed(false);
      if (shownTask.current !== loadingTask) loadingTask.destroy();
    };
  }, [url]);

  // Released once the pages have moved to the newer document.
  useEffect(() => {
    replacedTasks.current.forEach((task) => task.destroy());
    replacedTasks.current = [];
  }, [pdf]);

  useEffect(
    () => () => {
      shownTask.current?.destroy();
      replacedTasks.current.forEach((task) => task.destroy());
    },
    []
  );

  if (failed) {
    return (
      <div className="flex justify-center py-10">
        <Text variant="bodySecondary" data-test="proposal-document-error">
          {t("contracts.proposals.editor.documentError")}
        </Text>
      </div>
    );
  }

  if (!pdf) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {Array.from({ length: pdf.numPages }, (_, index) => (
        <PdfPage
          key={index}
          pdf={pdf}
          pageNumber={index + 1}
          width={width}
          onError={onPageError}
          renderOverlay={renderOverlay}
        />
      ))}
    </div>
  );
};
